import React, {
    useEffect, useContext, useState, useCallback
} from 'react';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { TranslatorContext, IntlContext } from '@jutro/locale';
import _ from 'lodash';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { withRouter, Link } from 'react-router-dom';
import { ClaimService } from 'gw-capability-claim';
import { useAuthentication } from 'gw-digital-auth-react';
import { ModalNextProvider, InfoLabel } from '@jutro/components';
import { error } from '@jutro/logger';
import { ImageComponent } from 'gw-components-platform-react';
import { LobIconUtil } from 'gw-portals-util-js';
import ClaimLobOptionsMenu from '../ClaimLobOptionsMenu/ClaimLobOptionsMenu';
import metadata from './ClaimListComponent.metadata.json5';
import styles from '../../pages/ClaimList/ClaimList.module.scss';
import claimsMessages from '../../Claims.messages';
import messages from './ClaimListComponent.messages';

function ClaimListComponent({ history }) {
    const { authHeader } = useAuthentication();
    const { claimsFilterLOBOptions } = appConfig;
    const [claimDataTable, setClaimDataTable] = useState('');
    const [isLoading, setLoadingState] = useState(false);

    const [selectedClaimLOB, setSelectedClaimLOBe] = useState(undefined);
    const [searchFilter, setSearchFilter] = useState(undefined);
    const [isClosed, setIsClosed] = useState(false);
    const translator = useContext(TranslatorContext);
    const intl = useContext(IntlContext);

    const processClaimsData = useCallback(
        (claimsData) => {
            if (!_.isEmpty(claimsData)) {
                let claimsArrayResult = claimsData;
                claimsArrayResult = claimsArrayResult.map((claimInfo) => {
                    const claims = {
                        product: claimInfo.lineOfBusinessCode,
                        claimType: claimInfo.claimTypeTDIC,
                        offeringName: claimInfo.offeringName,
                        offeringCode: claimInfo.offeringCode,
                        claimNumber: claimInfo.claimNumber,
                        isSubmitted: claimInfo.portalClaimSubmitted,
                        dateOfLoss: intl.formatDate(new Date(claimInfo.lossDate), { year: 'numeric', month: 'short', day: 'numeric' }),
                        status: translator({
                            id: `typekey.ClaimState.${claimInfo.claimState}`,
                            defaultMessage: claimInfo.claimState
                        }),
                        policyNumber: claimInfo.policyNumber,
                        reportedDate: intl.formatDate(new Date(claimInfo.reportedDate), { year: 'numeric', month: 'short', day: 'numeric' }),
                        claimantName: claimInfo.claimantName,
                    };
                    return claims;
                });
                return claimsArrayResult;
            }
            return [];
        },
        [intl, translator],
    );
    const getClaims = useCallback(
        (query) => {
            const claimsPromise = ClaimService.getClaimSummaries(query, {}, authHeader);
            return claimsPromise
                .then((responseData) => {
                    const data = processClaimsData(responseData.items);
                    setClaimDataTable(data);
                }).catch((err) => {
                    error(`ClaimListComponent: getClaims - Unable to get claims ${JSON.stringify(err)}`);
                    ModalNextProvider.showAlert({
                        title: messages.claimRequestFailed,
                        message: messages.CanNotLoadClaimInformation,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: messages.ok
                    }).then(() => {
                        history.push('/error');
                    }, _.noop);
                }).finally(() => {
                    setLoadingState(false);
                });
        },
        [authHeader, processClaimsData, history]
    );
    const loadClaimData = useCallback(
        (filters) => {
            setLoadingState(true);

            let claimStates = isClosed ? ['open', 'draft', 'closed'] : ['open', 'draft'];
            let policyType = selectedClaimLOB !== translator(claimsMessages.allClaims)
                ? selectedClaimLOB
                : undefined;
            let queryText = searchFilter;

            if (!_.isUndefined(filters)) {
                if (!_.isUndefined(filters.isClosed)) {
                    claimStates = filters.isClosed
                        ? ['open', 'draft', 'closed']
                        : ['open', 'draft'];
                }
                if (!_.isUndefined(filters.policyType)) {
                    policyType = filters.policyType !== translator(claimsMessages.allClaims)
                        ? filters.policyType
                        : undefined;
                }
                queryText = !_.isUndefined(filters.queryText) ? filters.queryText : queryText;
            }

            const query = {
                claimStates: claimStates,
                policyType: policyType,
                queryText: queryText
            };

            return getClaims(query);
        },
        [selectedClaimLOB, searchFilter, isClosed, getClaims, translator]
    );

    useEffect(() => {
        loadClaimData();
        setSelectedClaimLOBe(translator(claimsMessages.allClaims));

        // Disabling to prevent continues re-rendering
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilterChange = useCallback(
        (value, id) => {
            if (id === 'LOBFilter' && value !== 'Filters') {
                setSelectedClaimLOBe(value);
                loadClaimData({ policyType: value });
            }
            if (id === 'searchFilter') {
                loadClaimData({ queryText: value });
            }
            if (id === 'closedFilter') {
                setIsClosed(value);
                loadClaimData({ isClosed: value });
            }
        },
        [loadClaimData]
    );

    const getCell = useCallback(
        (item, index, property) => {
            const toolTipMessage = {
                product: translator(claimsMessages.line),
                dateOfLoss: translator(claimsMessages.dateOfLossClaimDetail)
            };
            return <span title={toolTipMessage[property.id]}>{item[property.id]}</span>;
        },
        [translator]
    );

    const getPolicyType = useCallback((item, index, property) => {
        const mappedOfferingCode = _.get(appConfig, `TDICOfferingCodeMapCCPC.${item[property.id]}`, '');
        return _.get(appConfig, `TDICAbbreviations.${mappedOfferingCode}`, '');
    }, []);

    const getStatusCell = useCallback(
        (item, index, property) => {
            const titleMessage = translator(claimsMessages.claimStatus);
            const status = item[property.id];
            let typeStatus = 'success';

            if (status === translator({ id: 'typekey.ClaimState.draft', defaultMessage: 'draft' })) {
                typeStatus = 'warning';
            } else if (status === translator({ id: 'typekey.ClaimState.closed', defaultMessage: 'closed' })) {
                typeStatus = 'neutral';
            }

            return (
                <InfoLabel type={typeStatus} size="small" title={titleMessage[property.id]}>
                    {item[property.id]}
                </InfoLabel>
            );
        },
        [translator]
    );

    const getLink = useCallback(
        (item, index, property) => {
            if (
                property.id === 'claimNumber' && item.isSubmitted !== 'Yes'
                && item.status
                === translator({ id: 'typekey.ClaimState.draft', defaultMessage: 'draft' })
            ) {
                const redirectpath = {
                    pathname: '/fnol-select-policy',
                    state: {
                        claimNumber: item[property.id],
                        redirectPath: '/claims',
                        claimStatus: item.status
                    }
                };
                return (
                    <Link
                        to={redirectpath}
                        className={styles.gwClaimsLinkContent}
                        title={translator(claimsMessages.inComplete)}
                    >
                        {item[property.id]}
                    </Link>
                );
            }
            if (
                property.id === 'claimNumber'
                && (item.status
                === translator({ id: 'typekey.ClaimState.open', defaultMessage: 'open' }) || item.status === translator({ id: 'typekey.ClaimState.closed', defaultMessage: 'closed' }))
            ) {
                const redirectRoute = property.id === 'policyNumber'
                    ? `/claims/policy/${item.claimNumber}`
                    : `/claims/${item[property.id]}`;
                const redirectPath = {
                    pathname: redirectRoute,
                    state: {
                        claimNumber: item[property.id],
                        redirectPath: '/claims',
                        claimStatus: item.status
                    }
                };
                return (
                    <Link
                        to={redirectPath}
                        className={styles.gwClaimsLinkContent}
                    >
                        {item[property.id]}
                    </Link>
                );
            }
            return (
                <div
                    className={styles.gwClaimsLinkContent}
                    title={translator(claimsMessages.completed)}
                >
                    {item.claimNumber}
                </div>
            );
        },
        [translator]
    );

    const getProductIcon = useCallback((item) => {
        const offeringCode = item.offeringName;
        const lineOfBusiness = item.offeringCode;
        return (
            <ImageComponent
                src={`/branding/${LobIconUtil.getImageIcon(lineOfBusiness, offeringCode)}.svg`}
                alt={lineOfBusiness}
                title={item.offeringName}
            />
        );
    }, []);

    const handleFileClick = useCallback(() => {
        return history.push({
            pathname: '/fnol-select-policy',
            state: {
                redirectPath: '/claims'
            }
        });
    }, [history]);

    const overrides = {
        claimListTableLoader: {
            loaded: !isLoading
        },
        claimTable: {
            data: claimDataTable,
            visible: !isLoading
        },
        searchFilter: {
            onValueChange: setSearchFilter,
            value: searchFilter,
            onEnter: () => {
                handleFilterChange(searchFilter, 'searchFilter');
            }
        },
        noDataMessageContainer: {
            visible: _.isEmpty(claimDataTable) && !isLoading
        },
        noDataMessage: {
            value: translator(claimsMessages.noClaimsResult)
        },
        claimLobOptionsMenu: {
            values: [translator(claimsMessages.allClaims), ...claimsFilterLOBOptions],
            handleFilterChange: handleFilterChange,
            selectedClaimLOB: selectedClaimLOB,
            isShowClosedClaimFilter: true
        },
        closedClaims: {
            value: isClosed,
            onValueChange: (value) => {
                handleFilterChange(value, 'closedFilter');
            }
        },
        fileClaimButton: {
            to: handleFileClick
        }
    };
    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            getCell: getCell,
            getPolicyType: getPolicyType,
            getLink: getLink,
            getProductIcon: getProductIcon,
            getStatusCell: getStatusCell,
            handleFilterValue: handleFilterChange,
            handleClosedClaims: setIsClosed,
            handleFileClick: handleFileClick
        },
        resolveComponentMap: {
            claimloboptionsmenu: ClaimLobOptionsMenu
        }
    };

    return renderContentFromMetadata(metadata.componentContent, overrides, resolvers);
}

export default withRouter(ClaimListComponent);
